import React, { useCallback, useEffect, useId, useRef } from "react";
import CustomFormField, { getFieldIds } from "../CustomFormField/CustomFormField";
import "../CustomTheme/customTheme.css";
import "./CustomTextArea.css";

/**
 * CustomTextArea — multi-line text input.
 *
 * Props:
 *  - label | title, name, id, isRequired, hint, error, colClass
 *  - value, onChange(value) | setInputText(value), placeholder, disabled
 *  - rows        visible rows (default 3)
 *  - maxLength   hard limit; shows a "12 / 200" counter
 *  - showCount   force the counter on/off (default: on when maxLength is set)
 *  - autoResize  grow with the content instead of scrolling (default false)
 *  - resize      CSS resize: "vertical" (default) | "none" | "both"
 *  - className / style apply to the <textarea>
 */
const CustomTextArea = ({
  label,
  title,
  name,
  id,
  isRequired,
  hint,
  error,
  colClass,
  noMargin = false,
  value,
  onChange,
  setInputText,
  placeholder,
  disabled,
  rows = 3,
  maxLength,
  showCount,
  autoResize = false,
  resize = "vertical",
  className,
  style,
  ...rest
}) => {
  const autoId = useId();
  const inputId = id || name || `cta-${autoId}`;
  const ids = getFieldIds(inputId);
  const ref = useRef(null);

  const text = value ?? "";
  const length = String(text).length;
  const counterOn = showCount ?? Boolean(maxLength);
  const atLimit = Boolean(maxLength) && length >= maxLength;

  const fitHeight = useCallback(() => {
    if (!autoResize || !ref.current) return;
    ref.current.style.height = "auto";
    ref.current.style.height = `${ref.current.scrollHeight}px`;
  }, [autoResize]);

  useEffect(() => {
    fitHeight();
  }, [text, fitHeight]);

  const handleChange = (e) => {
    onChange?.(e.target.value);
    setInputText?.(e.target.value);
  };

  return (
    <CustomFormField
      label={label ?? title}
      isRequired={isRequired}
      htmlFor={inputId}
      hint={hint}
      error={error}
      colClass={colClass}
      noMargin={noMargin}
    >
      <textarea
        ref={ref}
        id={inputId}
        name={name}
        rows={rows}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        onChange={handleChange}
        aria-invalid={Boolean(error)}
        aria-required={Boolean(isRequired)}
        aria-describedby={ids.describedBy(error, hint)}
        className={`cui-control cta-textarea${error ? " cui-control-error" : ""}${
          className ? ` ${className}` : ""
        }`}
        style={{ resize: autoResize ? "none" : resize, ...style }}
        {...rest}
      />

      {counterOn && (
        <span className={`cta-counter${atLimit ? " cta-counter-limit" : ""}`}>
          {length}
          {maxLength ? ` / ${maxLength}` : ""}
        </span>
      )}
    </CustomFormField>
  );
};

export default CustomTextArea;
